/* eslint-disable no-console */
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import { jwtDecode } from 'jwt-decode';

import { client } from '@/services/client';

function configureCookieStorage() {
  const { protocol, hostname } = globalThis.location;

  cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage({
    domain: hostname === 'localhost' ? hostname : '.intellivend.app',
    secure: protocol.startsWith('https'),
    path: '/',
    expires: 30,
    sameSite: 'lax',
  }));
}

interface UserPoolConfig {
  userPoolId: string;
  nativeClientId: string;
  customerOidcClientId?: string;
  kmhOidcClientId?: string;
  domainPrefix: string;
  redirectSignInUrls: string[];
  redirectSignOutUrls: string[];
  customerProviderName?: string;
  kmhProviderName?: string;
}

interface IdTokenPayload {
  sub: string;
  email?: string;
  email_verified?: boolean;
  [key: string]: unknown;
}

const configStorageKey = 'UserPoolConfigV3';
let globalConfig: UserPoolConfig | null = null;

function isValidUserPoolConfig(config: unknown): config is UserPoolConfig {
  if (!config || typeof config !== 'object') {
    return false;
  }
  const c = config as Record<string, unknown>;
  return typeof c.userPoolId === 'string' && c.userPoolId.length > 0
    && typeof c.nativeClientId === 'string' && c.nativeClientId.length > 0
    && typeof c.domainPrefix === 'string' && c.domainPrefix.length > 0;
}

export function clearUserPoolConfigCache() {
  globalConfig = null;
  localStorage.removeItem(configStorageKey);
}

interface CachedConfig {
  config: UserPoolConfig;
  timestamp: number;
}

const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

const SELECTED_CLIENT_KEY = 'amplify:selectedClient'; // 'native' | 'customer' | 'kmh'

function setSelectedClient(kind: 'native' | 'customer' | 'kmh') {
  localStorage.setItem(SELECTED_CLIENT_KEY, kind);
}

function resolveClientId(c: UserPoolConfig) {
  const kind = localStorage.getItem(SELECTED_CLIENT_KEY) as 'native' | 'customer' | 'kmh' | null;

  if (kind === 'kmh') {
    return c.kmhOidcClientId!;
  }

  if (kind === 'customer') {
    return c.customerOidcClientId!;
  }

  return c.nativeClientId;
}

function configureAmplify(configuration: UserPoolConfig) {
  const userPoolClientId = resolveClientId(configuration);

  if (!userPoolClientId) {
    console.error('[Amplify] Resolved userPoolClientId is undefined. Config:', configuration);
  }

  const {
    userPoolId,
    redirectSignInUrls,
    redirectSignOutUrls,
    domainPrefix,
    customerProviderName,
    kmhProviderName,
  } = configuration;

  const providers: Array<{ custom: string }> = [];
  if (customerProviderName) { providers.push({ custom: customerProviderName }); }
  if (kmhProviderName) { providers.push({ custom: kmhProviderName }); }

  const currentOrigin = globalThis.location.origin;
  const finalRedirectSignInUrls = redirectSignInUrls?.includes(currentOrigin)
    ? redirectSignInUrls
    : [currentOrigin, ...(redirectSignInUrls || [])];
  const finalRedirectSignOutUrls = redirectSignOutUrls?.includes(currentOrigin)
    ? redirectSignOutUrls
    : [currentOrigin, ...(redirectSignOutUrls || [])];

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          oauth: {
            domain: `${domainPrefix}.auth.eu-central-1.amazoncognito.com`,
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: finalRedirectSignInUrls,
            redirectSignOut: finalRedirectSignOutUrls,
            responseType: 'code',
            providers,
          },
          username: true,
          email: true,
          phone: false,
        },
      },
    },
  });

  // needs to be called right after Amplify.configure()
  configureCookieStorage();
}

async function getUserPoolConfig(): Promise<UserPoolConfig> {
  if (globalConfig) {
    return globalConfig;
  }

  const storageConfig = localStorage.getItem(configStorageKey);
  if (storageConfig) {
    try {
      const { config: cachedData, timestamp } = JSON.parse(storageConfig) as CachedConfig;
      const isExpired = Date.now() - timestamp > CACHE_TTL_MS;

      if (!isExpired) {
        if (isValidUserPoolConfig(cachedData)) {
          globalConfig = cachedData;
          return globalConfig;
        }
        console.warn('[Amplify][Cockpit] Cached config is invalid or corrupt, clearing and refetching');
        localStorage.removeItem(configStorageKey);
      } else {
        console.debug('[Amplify][Cockpit] Cached config expired, fetching new config');
      }
    } catch {
      console.warn('[Amplify][Cockpit] Failed to parse stored config, clearing cache');
      localStorage.removeItem(configStorageKey);
    }
  }

  try {
    const fetched = await client.publicGet<UserPoolConfig>('/config/tenant');
    if (!fetched) {
      throw new Error('User pool configuration not found. Please ensure the tenant config is set up correctly.');
    }

    globalConfig = fetched;
    const cachedConfig: CachedConfig = {
      config: fetched,
      timestamp: Date.now(),
    };
    localStorage.setItem(configStorageKey, JSON.stringify(cachedConfig));
    return fetched;
  } catch (error) {
    console.error('[Amplify][Cockpit] Failed to fetch user pool config:', error);
    throw error;
  }
}

export async function configureAmplifyFromTenantConfig() {
  globalConfig = await getUserPoolConfig();
  configureAmplify(globalConfig);
}

export async function getJwtTokens() {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    throw new Error('ID token not found');
  }
  const accessTokenClientID = session.tokens?.accessToken?.payload?.client_id as string | undefined;
  const decodedIDToken = jwtDecode<IdTokenPayload>(idToken);
  return { decodedIDToken, idToken, accessTokenClientID };
}

// Support client enforcement
// Comma separated list of Cognito App Client IDs that represent KMH support access flows.
const supportClientIds = new Set(
  (import.meta.env.VITE_SUPPORT_CLIENT_IDS || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean),
);

function isSupportClient(clientId: string | undefined): boolean {
  return !!clientId && supportClientIds.has(clientId);
}

export function isSupportAccessValid(idTokenPayload: IdTokenPayload): boolean {
  const clientId = (idTokenPayload as Record<string, unknown>).client_id as string | undefined;
  if (!isSupportClient(clientId)) {
    // Not a support client → no validation needed
    return true;
  }
  const raw = idTokenPayload['custom:support_expiry'];
  if (typeof raw !== 'string' || !raw.trim()) {
    return false;
  }
  // Accept date-only (YYYY-MM-DD) or full ISO timestamp
  let expiry: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    // Treat date-only as end of that day (UTC)
    expiry = new Date(`${raw}T23:59:59.999Z`);
  } else {
    expiry = new Date(raw);
    if (Number.isNaN(expiry.getTime())) {
      return false;
    }
  }

  return expiry.getTime() > Date.now();
}

// Returns all OIDC (non-native) client IDs from the tenant config if available.
// Falls back to localStorage cache if in-memory config not yet initialized.
export function getOidcClientIds(): string[] {
  if (!globalConfig) {
    const storageConfig = localStorage.getItem(configStorageKey);
    if (storageConfig) {
      try {
        const { config: cachedData } = JSON.parse(storageConfig) as CachedConfig;
        globalConfig = cachedData;
      } catch {
        console.warn('[Amplify][Cockpit] Failed to parse stored config while resolving OIDC client IDs');
      }
    }
  }
  const ids: string[] = [];
  if (globalConfig?.customerOidcClientId) { ids.push(globalConfig.customerOidcClientId); }
  if (globalConfig?.kmhOidcClientId) { ids.push(globalConfig.kmhOidcClientId); }
  return ids;
}

export function hasCustomerOidcClientId(): boolean {
  if (!globalConfig) {
    const storageConfig = localStorage.getItem(configStorageKey);
    if (storageConfig) {
      try {
        const { config: cachedData, timestamp } = JSON.parse(storageConfig) as CachedConfig;
        const isExpired = Date.now() - timestamp > CACHE_TTL_MS;
        if (!isExpired) {
          globalConfig = cachedData;
        }
      } catch {
        console.warn('[Amplify][Cockpit] Failed to parse stored config while resolving customer OIDC client ID');
      }
    }
  }

  return !!globalConfig?.customerOidcClientId;
}

export function resetToNativeClient() {
  setSelectedClient('native');
}

export const amplifyAuthService = {
  async claimSSOUser() {
    const { decodedIDToken, idToken, accessTokenClientID } = await getJwtTokens();

    const allowedOidcClientIds = getOidcClientIds();
    const isOidcClient = !!accessTokenClientID && allowedOidcClientIds.includes(accessTokenClientID);
    const ssoUnconfigured = !decodedIDToken['custom:sso_configured'] || decodedIDToken['custom:sso_configured'] !== 'true';

    return {
      isInitialSSOLoginRequired: isOidcClient && ssoUnconfigured,
      isInvalidSupportAccess: !isSupportAccessValid(decodedIDToken),
      idToken,
    };
  },

  async useCustomerOidcLoginConfig() {
    setSelectedClient('customer');
    await configureAmplifyFromTenantConfig();
    if (!globalConfig?.customerOidcClientId) {
      console.error('[Amplify][Cockpit] Customer OIDC Client ID missing after config fetch');
    }
  },

  async useKMHOidcLoginConfig() {
    setSelectedClient('kmh');
    await configureAmplifyFromTenantConfig();
    if (!globalConfig?.kmhOidcClientId) {
      console.error('[Amplify][Cockpit] KMH OIDC Client ID missing after config fetch');
    }
  },
};

// For testing: reset cached config state
export function resetAuthState() {
  globalConfig = null;
}
